<script setup>
import { ref, watch } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import AutoComplete from 'primevue/autocomplete';
import { useServerTable } from '@/composables/useServerTable.js';
import { useContactsStore } from '@/stores/contact.js';
import customerApi from '@/api/customerApi.js';
import facilityApi from '@/api/facilityApi.js';
import contractApi from '@/api/contractApi.js';
import workApi from '@/api/workApi.js';

const props = defineProps({
  search: { type: String, default: '' },
});

const selectedCustomer = ref(null);
const selectedFacility = ref(null);
const selectedContract = ref(null);
const selectedWork = ref(null);

const customerSuggestions = ref([]);
const facilitySuggestions = ref([]);
const contractSuggestions = ref([]);
const workSuggestions = ref([]);

const contactsStore = useContactsStore();

const {
  data: contacts,
  totalRecords,
  loading,
  rowsPerPage,
  setParams,
  currentPage,
  load,
  setSearch,
  setExtra,
  onPageChange,
  firstIndex,
} = useServerTable({
  initialRows: 10,
  debounceMs: 500,
  loadPage: async ({ page, search, ...extra }) => {
    const resp = await contactsStore.loadContactsList({
      page,
      search,
      customer: extra.customer_id,
      facility: extra.facility_id,
      contract: extra.contract_id,
      work: extra.work_id,
    });
    const results = Array.isArray(resp?.results) ? resp.results : [];
    const denorm = results.map((c) => {
      const links = Array.isArray(c.links) ? c.links : [];
      const by = (type) => (links.find((l) => l?.target_type === type)?.short_name) || '';
      return {
        ...c,
        customer_name: by('customer'),
        facility_name: by('facility'),
        contract_name: by('contract'),
        work_name: by('work'),
      };
    });
    return { ...resp, results: denorm };
  },
});

watch(
    () => props.search,
    (val, prev) => {
      const isInitialLoad = prev === undefined;
      if (!isInitialLoad && val === prev) return;

      selectedCustomer.value = null;
      selectedFacility.value = null;
      selectedContract.value = null;
      selectedWork.value = null;

      setParams({
        search: (val || '').trim(),
        extra: { customer_id: undefined, facility_id: undefined, contract_id: undefined, work_id: undefined },
        resetPage: true,
        immediate: isInitialLoad, // Первая загрузка - немедленно, остальные - с debounce
      });
    },
    { immediate: true }
);




function handleExclusiveSelect(kind, value) {
  if (kind === 'customer') {
    selectedCustomer.value = value;
    selectedFacility.value = null;
    selectedContract.value = null;
    selectedWork.value = null;
    setExtra({ customer_id: value?.id, facility_id: undefined, contract_id: undefined, work_id: undefined });
    return;
  }
  if (kind === 'facility') {
    selectedFacility.value = value;
    selectedCustomer.value = null;
    selectedContract.value = null;
    selectedWork.value = null;
    setExtra({ customer_id: undefined, facility_id: value?.id, contract_id: undefined, work_id: undefined });
    return;
  }
  if (kind === 'contract') {
    selectedContract.value = value;
    selectedCustomer.value = null;
    selectedFacility.value = null;
    selectedWork.value = null;
    setExtra({ customer_id: undefined, facility_id: undefined, contract_id: value?.id, work_id: undefined });
    return;
  }
  if (kind === 'work') {
    selectedWork.value = value;
    selectedCustomer.value = null;
    selectedFacility.value = null;
    selectedContract.value = null;
    setExtra({ customer_id: undefined, facility_id: undefined, contract_id: undefined, work_id: value?.id });
  }
}

// Поиск опций (server-side)
async function searchCustomers(event) {
  const q = event.query || '';
  const resp = await customerApi.getCustomers({ page: 1, q, is_active: true });
  customerSuggestions.value = (resp?.results || []).map(it => ({ id: it.id, label: it.short_name || it.name || `#${it.id}` }));
}
async function searchFacilities(event) {
  const q = event.query || '';
  const resp = await facilityApi.getFacilities({ page: 1, status: 'ACTIVE', q: q || undefined });
  facilitySuggestions.value = (resp?.results || []).map(it => ({ id: it.id, label: it.short_name || it.name || `#${it.id}` }));
}
async function searchContracts(event) {
  const q = event.query || '';
  const resp = await contractApi.getContracts({ page: 1, q, status: 'ACTIVE' });
  contractSuggestions.value = (resp?.results || []).map(it => ({ id: it.id, label: it.number ? `№ ${it.number}` : `#${it.id}` }));
}
async function searchWorks(event) {
  const q = event.query || '';
  const resp = await workApi.getWorks({ page: 1, q, status: 'ACTIVE' });
  workSuggestions.value = (resp?.results || []).map(it => ({ id: it.id, label: it.name || `#${it.id}` }));
}
</script>

<template>
  <div class="flex flex-col gap-3 h-full">
    <DataTable
        :value="contacts"
        paginator
        :rows="rowsPerPage"
        :totalRecords="totalRecords"
        :loading="loading"
        :first="firstIndex()"
        @page="onPageChange"
        lazy
        stripedRows
        scrollable
        scrollHeight="flex"
        tableStyle="min-width: 50rem"
        class="cursor-pointer text-sm h-full"
        filterDisplay="row"
    >
      <!-- Контакт -->
      <Column
          header="Контакт"
          :headerStyle="{ width: '30%', maxWidth: '30%' }"
          :bodyStyle="{ width: '30%', maxWidth: '30%' }"
      >
        <template #body="{ data }">
          <div class="flex flex-col gap-0.5">
            <!-- Имя -->
            <div class="font-semibold break-words overflow-hidden">
              <span v-if="data?.name && String(data.name).trim().length">{{ data.name }}</span>
              <span v-else class="text-gray-400">Имя не заполнено</span>
            </div>

            <!-- Должность -->
            <div class="break-words overflow-hidden">
              <span v-if="data?.post && String(data.post).trim().length" class="text-gray-600">{{ data.post }}</span>
              <span v-else class="text-gray-400">Должность не указана</span>
            </div>

            <!-- Email -->
            <div class="break-words overflow-hidden">
              <a
                  v-if="data?.email && String(data.email).trim().length"
                  :href="`mailto:${data.email}`"
                  class="text-blue-600 hover:underline"
              >
                {{ data.email }}
              </a>
              <span v-else class="text-gray-400">Email не указан</span>
            </div>

            <!-- Телефон -->
            <div class="break-words overflow-hidden">
              <template v-if="data?.phone && String(data.phone).trim().length">
                <span>{{ data.phone }}</span>
              </template>
              <template v-else-if="Array.isArray(data?.phones) && data.phones.filter(p => String(p || '').trim().length).length">
                <span>{{ data.phones.filter(p => String(p || '').trim().length).join(', ') }}</span>
              </template>
              <span v-else class="text-gray-400">Телефон не указан</span>
            </div>
          </div>
        </template>
      </Column>

      <!-- Заказчик -->
      <Column
          header="Заказчик"
          :showFilterMenu="false"
          :headerStyle="{ width: '20%', maxWidth: '20%' }"
          :bodyStyle="{ width: '20%', maxWidth: '20%' }"
      >
        <template #body="{ data }">
          <span class="break-words overflow-hidden">{{ data.customer_name }}</span>
        </template>
        <template #filter>
          <div class="min-w-0 w-4/5" >
            <AutoComplete
                v-model="selectedCustomer"
                :suggestions="customerSuggestions"
                optionLabel="label"
                placeholder="Фильтр: Заказчик"
                forceSelection
                completeOnFocus
                @complete="searchCustomers"
                @update:modelValue="(val) => handleExclusiveSelect('customer', val)"
                :pt="{
                root: { class: 'w-full' },
                input: { class: 'h-6 py-0 text-sm' },
                overlay: { class: 'max-h-56' }
              }"
                class="w-full"
            />
          </div>
        </template>
      </Column>

      <!-- Объект -->
      <Column
          header="Объект"
          :showFilterMenu="false"
          :headerStyle="{ width: '18%', maxWidth: '18%' }"
          :bodyStyle="{ width: '18%', maxWidth: '18%' }"
      >
        <template #body="{ data }">
          <span class="break-words overflow-hidden">{{ data.facility_name }}</span>
        </template>
        <template #filter>
          <div class="min-w-0">
            <AutoComplete
                v-model="selectedFacility"
                :suggestions="facilitySuggestions"
                optionLabel="label"
                placeholder="Фильтр: Объект"
                forceSelection
                completeOnFocus
                @complete="searchFacilities"
                @update:modelValue="(val) => handleExclusiveSelect('facility', val)"
                :pt="{
                root: { class: 'w-full' },
                input: { class: 'h-6 py-0 text-sm' },
                overlay: { class: 'max-h-56' }
              }"
                class="w-full"
            />
          </div>
        </template>
      </Column>

      <!-- Договор -->
      <Column
          header="Договор"
          :showFilterMenu="false"
          :headerStyle="{ width: '17%', maxWidth: '17%' }"
          :bodyStyle="{ width: '17%', maxWidth: '17%' }"
      >
        <template #body="{ data }">
          <span class="break-words overflow-hidden">{{ data.contract_name }}</span>
        </template>
        <template #filter>
          <div class="min-w-0">
            <AutoComplete
                v-model="selectedContract"
                :suggestions="contractSuggestions"
                optionLabel="label"
                placeholder="Фильтр: Договор"
                forceSelection
                completeOnFocus
                @complete="searchContracts"
                @update:modelValue="(val) => handleExclusiveSelect('contract', val)"
                :pt="{
                root: { class: 'w-full' },
                input: { class: 'h-6 py-0 text-sm' },
                overlay: { class: 'max-h-56' }
              }"
                class="w-full"
            />
          </div>
        </template>
      </Column>

      <!-- Работа -->
      <Column
          header="Работа"
          :showFilterMenu="false"
          :headerStyle="{ width: '15%', maxWidth: '15%' }"
          :bodyStyle="{ width: '15%', maxWidth: '15%' }"
      >
        <template #body="{ data }">
          <span class="break-words overflow-hidden">{{ data.work_name }}</span>
        </template>
        <template #filter>
          <div class="min-w-0">
            <AutoComplete
                v-model="selectedWork"
                :suggestions="workSuggestions"
                optionLabel="label"
                placeholder="Фильтр: Работа"
                forceSelection
                completeOnFocus
                @complete="searchWorks"
                @update:modelValue="(val) => handleExclusiveSelect('work', val)"
                :pt="{
                root: { class: 'w-full' },
                input: { class: 'h-6 py-0 text-sm' },
                overlay: { class: 'max-h-56' }
              }"
                class="w-full"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
/* Убираем горизонтальный скролл у таблицы */
:deep(.p-datatable-wrapper) {
  overflow-x: hidden !important;
}

/* Перенос текста и уважение ширины столбцов */
:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
  background: transparent !important;
  border: none !important;
}

/* Сжимаем фильтры в строке фильтров */
:deep(.p-datatable .p-column-header-content) {
  align-items: flex-start;
}
:deep(.p-datatable .p-column-filter) {
  padding: 0 !important;
}
:deep(.p-autocomplete .p-inputtext) {
  height: 1.5rem !important; /* ~ h-7 */
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 0.75rem !important; /* text-sm */
  line-height: 1.25rem !important;
  width: 90%;
}
:deep(.p-autocomplete .p-autocomplete-panel) {
  max-height: 14rem; /* ограничим выпадающий список */
  overflow-y: auto;
}

/* Чуть компактнее строки таблицы */
:deep(.p-datatable-tbody > tr > td) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
</style>
